<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:link',
	name: '$t:blocks.input_list_o2m.name',
	description: '$t:blocks.input_list_o2m.description',
	category: 'input',
	tags: ['form-control'],
	options: [
		{
			name: 'fieldId',
		},
		{
			name: 'referencedItemKey',
		},
		{
			name: 'disabled',
		},
		{
			name: 'readonly',
		},
		{
			name: 'disabledNotice',
		},
	],

	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { DefaultViewOptions } from '#layers/blocks/blocks/Listing.block.vue';
import type { CompositePrimaryKey, GlobalSingleRelationFieldId } from '#layers/data-model';
import type { ButtonProps } from '#layers/ui';
import type { RelatedDisplayItem, RelatedItemState } from '~/components/RelatedItemListCard.vue';
import type { CreateItem } from '~/composables/form-block/use-relation-field-state';
import type { O2MStateHandlers } from '~/composables/use-o2m-state';
import type { InputBlockProps } from '~/types/input-block';
import { computed, defineAsyncComponent, ref, shallowRef, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOverlay } from '#imports';
import { omit } from '#layers/base/utils/object';
import { defineBlock } from '#layers/block-engine';
import { pickByPrimitiveFields } from '#layers/data-model/utils/item';
import { stringifyPrimaryKey } from '#layers/data-model/utils/primary-key';
import { getCollectionPrimaryKeyFields } from '#layers/data-model/utils/schema/collection';
import RelatedItemListCard from '~/components/RelatedItemListCard.vue';
import { useDrawerFieldOverrides, useRelationFormField } from '~/composables/form-block';
import { injectFormBlockContext } from '~/composables/form-block/form-context';
import { createdItemKey, isCreatedItem, markCreatedItem } from '~/composables/form-block/use-relation-field-state';
import { useO2MPermissions } from '~/composables/use-o2m-permissions';
import { useO2MState } from '~/composables/use-o2m-state';

type O2MActionProps = Partial<Omit<ButtonProps, 'onClick'>> & { onClick?: (o2mApi: O2MStateHandlers) => void };

export interface InputListO2MBlockProps extends Pick<InputBlockProps, 'disabled' | 'disabledNotice' | 'readonly'> {
	fieldId: GlobalSingleRelationFieldId;
	referencedItemKey: CompositePrimaryKey;
	defaultView?: DefaultViewOptions;
	createAction?: O2MActionProps;
	editShown?: boolean;
}

const props = withDefaults(defineProps<InputListO2MBlockProps>(), {
	editShown: true,
});

const LazyCollectionDetailDrawer = defineAsyncComponent(() => import('~/components/Collection/CollectionDetailDrawer.vue'));
const LazyCollectionListingDrawer = defineAsyncComponent(() => import('~/components/Collection/CollectionListingDrawer.vue'));

;

const { t } = useI18n();

const listCard = useTemplateRef('listCard');

const relationFieldContext = useRelationFormField<'to-many'>(props);
const { field, disabled, disabledNotice } = relationFieldContext;
const fieldOverrides = useDrawerFieldOverrides(field, computed(() => field.value?.oppositeCollection ?? null));
const primaryKeyFields = computed(() => field.value?.oppositeCollection ? getCollectionPrimaryKeyFields(field.value.oppositeCollection) : []);

const formBlockContext = injectFormBlockContext();
const permissions = useO2MPermissions({
	field,
	isCreateForm: () => formBlockContext.isCreate.value,
});

const pageSize = ref(25);

const o2mState = useO2MState({
	...relationFieldContext,
	pageSize,
});
const {
	items,
	pageNumber,
	totalItemsCount,
	currentItemsCount,
	isLoading,
	createItem,
	undoCreateItem,
	connectItem,
	undoConnectItem,
	disconnectItem,
	undoDisconnectItem,
	updateItem,
	selectedItemsFilter,
	getItemKey,
	isNewlyConnected,
	isNewlyCreated,
	isNewlyDisconnected,
	isNewlyUpdated,
} = o2mState;

const createActionProps = computed<Partial<ButtonProps>>(() => ({
	...omit(props.createAction ?? {}, ['onClick']),
}));

function getItemKeyString(item: CreateItem) {
	const key = stringifyPrimaryKey(pickByPrimitiveFields(item, primaryKeyFields.value));
	if (key) return key;
	if (isCreatedItem(item)) return item[createdItemKey];
	return '';
}

function getItemState(item: CreateItem): RelatedItemState {
	if (isNewlyCreated(item)) return 'created';
	if (isNewlyConnected(item)) return 'connected';
	if (isNewlyDisconnected(item)) return 'disconnected';
	if (isNewlyUpdated(item)) return 'updated';
	return 'default';
}

async function handleSelect() {
	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		props: {
			collectionId: field.value!.oppositeCollectionId,
			selectionMode: 'multiple',
			filter: {
				_not: selectedItemsFilter.value,
			},
			defaultView: props.defaultView,
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result) {
		for (const itemKey of result.selection) {
			connectItem(itemKey);
		}
	}
}

function handleRemove(item: CreateItem) {
	if (isCreatedItem(item)) {
		undoCreateItem(item);
	}
	else {
		const itemKey = getItemKey(item);
		disconnectItem(itemKey);
	}
}

function handleUndo(item: CreateItem) {
	const state = getItemState(item);
	const itemKey = getItemKey(item);

	switch (state) {
		case 'created':
			undoCreateItem(item);
			break;
		case 'connected':
			undoConnectItem(itemKey);
			break;
		case 'disconnected':
			undoDisconnectItem(itemKey);
			break;
	}
}

async function handleEdit(item: CreateItem) {
	const itemKey = getItemKey(item);
	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		props: {
			collectionId: field.value!.oppositeCollectionId,
			title: t('common.edit_item'),
			formMode: 'emit',
			itemEdits: item,
			itemId: !isCreatedItem(item) ? itemKey : undefined,
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result as { data: any } | undefined;

	if (payload) {
		if (isCreatedItem(item)) {
			createItem(markCreatedItem(payload.data, item));
		}
		else {
			updateItem(itemKey, payload.data);
		}
	}
}

async function handleCreate() {
	if (props.createAction?.onClick) {
		props.createAction.onClick(o2mState);
		return;
	}

	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		props: {
			collectionId: field.value!.oppositeCollectionId,
			title: t('common.create_item'),
			formMode: 'emit',
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result as { data: any } | undefined;

	if (payload) {
		createItem(payload.data);
		listCard.value?.scrollToBottom();
	}
}

const displayItems = shallowRef<RelatedDisplayItem<CreateItem>[]>([]);

function updateDisplayItems() {
	displayItems.value = items.value.map((item) => ({
		id: getItemKeyString(item),
		data: item,
		state: getItemState(item),
	}));
}

watch(items, () => {
	if (!formBlockContext.state.value?.$loading && !isLoading.value) {
		updateDisplayItems();
	}
}, {
	deep: true,
	immediate: true,
});
</script>

<template>
	<RelatedItemListCard
		ref="listCard"
		:items="displayItems"
		:collection-id="field!.oppositeCollectionId"
		:disabled
		:disabled-notice
		:readonly
		:aria-readonly="readonly"
		:show-edit="editShown"
		:create-action-props
		:pagination="{
			page: pageNumber,
			pageSize,
			totalItems: totalItemsCount,
			currentItems: currentItemsCount,
		}"
		:loading="isLoading"
		:permissions="{
			canConnect: permissions.canConnect.value,
			canCreate: permissions.canCreate.value,
			canEdit: permissions.canEdit.value,
			canDisconnect: permissions.canDisconnect.value,
		}"
		show-create
		show-add-existing
		@select="handleSelect"
		@create="handleCreate"
		@edit="handleEdit"
		@remove="handleRemove"
		@undo="handleUndo"
		@update:page="pageNumber = $event"
		@update:page-size="pageSize = $event"
	/>
</template>
